import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useApplicationStore } from '@/stores/application.store';
import { useUserStore } from '@/stores/user.store';

interface MenuItemsType {
    to: { name: string; params?: any };
    name: string;
    icon?: string;
    header?: string;
    children?: any;
    new?: boolean;
}

export function useSidebarLinks() {
    const applicationStore = useApplicationStore();
    const userStore = useUserStore();
    const { isFlagEnabled } = useFeatureFlags();

    const hasContracts = ref(true);

    const transaction = computed<MenuItemsType[]>(() => {
        const { isNotary, isSupport, isAdvisor } = userStore;

        const items = [
            {
                name: `NAVIGATION.${isSupport ? 'SALES' : 'INVOICES'}`,
                to: { name: 'transactions-sales' },
            },
        ];

        if (isNotary || isAdvisor || isSupport) {
            items.push({
                name: isSupport ? 'NAVIGATION.PURCHASE' : 'NAVIGATION.PAYOUT',
                to: { name: 'transactions-purchase' },
            });
        }

        if (isNotary || isSupport) {
            items.push({
                name: isSupport ? 'NAVIGATION.PAYOUT' : 'NAVIGATION.PAYOUT_BATCH',
                to: { name: 'transactions-payout' },
            });
        }

        if (isSupport) {
            items.push({
                name: 'NAVIGATION.CREDIT',
                to: { name: 'transactions-credit' },
            });
        }

        return items;
    });

    const stats: MenuItemsType[] = [
        {
            name: 'NAVIGATION.ACCOUNT_TRANSACTIONS',
            to: { name: 'account-transactions' },
        },
        {
            name: 'NAVIGATION.BOOKKEEPER_TRANSACTIONS',
            to: { name: 'bookkeeper-transactions' },
        },
        {
            name: 'NAVIGATION.BOOKKEEPER_LEADS',
            to: { name: 'bookkeeper-leads' },
        },
        {
            to: { name: 'processes' },
            name: 'GLOBAL.JOBS',
            icon: 'clipboard',
        },
    ];

    const contracts = computed<MenuItemsType>(() => {
        const type = userStore.isClient ? 'client' : 'advisor';

        return !isFlagEnabled('LEGACY_TEMPLATE_CONTRACT')
            ? hasContracts.value
                ? {
                      name: 'GLOBAL.CONTRACTS',
                      to: { name: `${type}-contracts` },
                      icon: 'contract',
                      children: [
                          {
                              name: 'GLOBAL.TEMPLATES',
                              to: { name: `${type}-templates` },
                          },
                          {
                              name: 'GLOBAL.CONTRACTS',
                              to: { name: `${type}-contracts` },
                          },
                      ],
                  }
                : {
                      name: 'GLOBAL.CONTRACTS',
                      to: { name: `${type}-templates` },
                      icon: 'contract',
                  }
            : {
                  name: 'GLOBAL.CONTRACTS',
                  to: { name: `${type}-contracts` },
                  icon: 'contract',
              };
    });

    const account: Record<string, MenuItemsType> = {
        settings: {
            name: 'USER_ACCOUNT.MY_ACCOUNT',
            to: { name: 'my-account' },
            icon: 'account',
            header: 'NAVIGATION.ACCOUNT',
        },
        employee: {
            name: 'USER_ACCOUNT.EMPLOYEES',
            to: { name: 'employees' },
            icon: 'users',
        },
        organization: {
            name: 'PAGE_TITLES.ORGANIZATION',
            to: { name: 'user-organization' },
            icon: 'building',
        },
        person: {
            name: 'FUNNEL.GLOBAL.CONTACTS',
            to: { name: 'user-contacts' },
            icon: 'contacts',
        },
        subscriptions: {
            name: 'USER_ACCOUNT.SUBSCRIPTIONS',
            to: { name: 'user-subscriptions' },
            icon: 'user-subscriptions',
        },
        persons: {
            name: 'FUNNEL.GLOBAL.CONTACTS',
            to: { name: 'user-contacts' },
            icon: 'contacts',
        },
        userReferrals: {
            name: 'USER_ACCOUNT.REFERRAL_PAGE',
            to: { name: 'user-referral' },
            icon: 'coupon',
        },
        processLabels: {
            name: 'USER_ACCOUNT.PROCESS_LABEL.LABEL',
            to: { name: 'user-process-labels' },
            icon: 'tags',
        },
    };

    const superAdmin: MenuItemsType[] = [
        {
            name: 'ADMIN_SETTINGS.TENANTS',
            to: { name: 'admin-tenants' },
        },
    ];

    const adminSettings = computed<MenuItemsType[]>(() => [
        {
            name: 'ADMIN_SETTINGS.ACCOUNTS',
            to: { name: 'admin-accounts' },
        },
        {
            name: 'ADMIN_SETTINGS.LICENSES',
            to: { name: 'admin-licenses' },
        },
        {
            name: 'ADMIN_SETTINGS.COUNTRIES',
            to: { name: 'admin-countries' },
        },
        {
            name: 'ADMIN_SETTINGS.CONTRACT_TEMPLATES',
            to: { name: 'admin-contract-templates' },
        },
        {
            name: 'ADMIN_SETTINGS.DOCUMENT_TAGS',
            to: { name: 'admin-document-tags' },
        },
        {
            name: 'ADMIN_SETTINGS.EMAILS',
            to: { name: 'admin-emails' },
        },
        {
            name: 'ADMIN_SETTINGS.LEGAL_TASK_TYPES',
            to: { name: 'admin-legal-task-types' },
        },
        {
            name: 'ADMIN_SETTINGS.FEEDBACK',
            to: { name: 'admin-feedback' },
        },
        {
            // TEMPORARY — Ligo migration cleanup, see AdminInvoices.vue
            name: 'ADMIN_SETTINGS.INVOICES',
            to: { name: 'admin-invoices' },
        },
        {
            name: 'ADMIN_SETTINGS.JOBS',
            to: { name: 'admin-jobs' },
        },
        {
            name: 'ADMIN_SETTINGS.JOB_CATEGORIES',
            to: { name: 'admin-job-categories' },
        },
        {
            name: 'ADMIN_SETTINGS.LICENSE_TYPES',
            to: { name: 'admin-license-types' },
        },
        {
            name: 'ADMIN_SETTINGS.PACKAGES',
            to: { name: 'admin-packages' },
        },
        {
            name: 'ADMIN_SETTINGS.PAYMENT_METHODS',
            to: { name: 'admin-payment-methods' },
        },
        {
            name: 'ADMIN_SETTINGS.PRODUCTS',
            to: { name: 'admin-products' },
        },
        {
            name: 'ADMIN_SETTINGS.REGIONS',
            to: { name: 'admin-regions' },
        },
        {
            name: 'ADMIN_SETTINGS.NOTARIES',
            to: { name: 'admin-notaries' },
        },
        {
            name: 'ADMIN_SETTINGS.RISK_WORDS',
            to: { name: 'admin-risk-words' },
        },
        {
            name: 'ADMIN_SETTINGS.SECTORS',
            to: { name: 'admin-sectors' },
        },
        {
            name: 'ADMIN_SETTINGS.SERVICE_PROVIDERS',
            to: { name: 'admin-service-providers' },
        },
        {
            name: 'ADMIN_SETTINGS.USERS',
            to: { name: 'admin-users' },
        },
        {
            name: 'ADMIN_SETTINGS.VERBAL_CONSENTS',
            to: { name: 'admin-verbal-consents' },
        },
        ...(userStore.isSuperAdmin ? superAdmin : []),
    ]);

    const support = computed<MenuItemsType[]>(() => {
        return [
            {
                to: { name: 'support-dashboard' },
                name: 'GLOBAL.JOBS',
                icon: 'clipboard',
            },
            {
                to: { name: 'support-entities' },
                name: 'GLOBAL.ENTITIES',
                icon: 'building-alt',
            },
            {
                name: 'NAVIGATION.ORGANIZATIONS',
                to: { name: 'support-organizations' },
                icon: 'briefcase',
            },
            {
                name: 'NAVIGATION.TRANSACTIONS',
                children: transaction.value,
                to: { name: 'bookkeeper-transactions' },
                icon: 'banknotes',
            },
            {
                name: 'NAVIGATION.STATS',
                to: { name: 'bookkeeper-transactions' },
                children: stats,
                icon: 'document-chart',
            },
            {
                name: 'NAVIGATION.SUBSCRIPTIONS',
                to: { name: 'support-subscriptions' },
                icon: 'receipt',
            },
            {
                name: 'NAVIGATION.LEADS',
                to: { name: 'support-leads' },
                icon: 'group',
            },
            {
                name: 'NAVIGATION.ADMIN',
                to: { name: 'admin-accounts' },
                children: adminSettings.value,
                icon: 'cog',
                header: 'NAVIGATION.ADMIN',
            },
            account.settings,
            account.employee,
            account.organization,
        ];
    });

    const notary = computed<MenuItemsType[]>(() => {
        return [
            {
                name: 'GLOBAL.JOBS',
                to: { name: 'notary-dashboard' },
                icon: 'clipboard',
            },
            {
                name: 'GLOBAL.ENTITIES',
                to: { name: 'notary-entities' },
                icon: 'building-alt',
            },
            {
                name: 'NAVIGATION.TRANSACTIONS',
                to: { name: 'transactions' },
                children: transaction.value,
                icon: 'banknotes',
            },
            account.settings,
            account.employee,
            account.organization,
            account.processLabels,
        ];
    });

    const bookkeeper = computed<MenuItemsType[]>(() => {
        return [
            {
                name: 'NAVIGATION.REQUESTED_SERVICES',
                icon: 'inbox-arrow',
                to: { name: 'advisor-dashboard' },
            },
            {
                name: 'GLOBAL.JOBS',
                to: { name: 'advisor-jobs' },
                icon: 'clipboard',
            },
            {
                name: 'GLOBAL.ENTITIES',
                to: { name: 'advisor-entities' },
                icon: 'building-alt',
            },
            {
                name: 'NAVIGATION.LEADS',
                to: { name: 'advisor-leads' },
                icon: 'group',
            },
            contracts.value,
            {
                name: 'NAVIGATION.INCORPORATION_BV',
                icon: 'building-alt',
                to: { name: 'start-incorporation', params: { type: 'bv' } },
                header: 'NAVIGATION.REQUEST_A_SERVICE',
            },
            {
                name: 'NAVIGATION.UBO',
                icon: 'notary',
                to: { name: 'start-ubo-registration', params: { type: 'ubo-registration' } },
            },
            {
                name: 'NAVIGATION.LIQUIDATION',
                icon: 'doc',
                to: { name: 'start-liquidation', params: { type: 'liquidation-bv' } },
            },
            {
                name: 'NAVIGATION.OTHER_SERVICES',
                icon: 'calculator',
                to: { name: 'start-general-flow' },
            },
            account.settings,
            {
                name: 'NAVIGATION.TRANSACTIONS',
                to: { name: 'transactions' },
                icon: 'banknotes',
                children: transaction.value,
            },
            account.employee,
            account.organization,
            account.persons,
            account.userReferrals,
        ];
    });

    const client = computed<MenuItemsType[]>(() => {
        const localItems: MenuItemsType[] = [
            {
                name: 'NAVIGATION.DASHBOARD',
                icon: 'rectangle-group',
                to: { name: 'client-dashboard' },
            },
            {
                name: 'NAVIGATION.PRODUCTS_AND_SERVICES',
                icon: 'mini-magnifying-glass',
                to: { name: 'start' },
            },
            {
                name: 'NAVIGATION.SHOP_DEALS',
                icon: 'tags',
                to: { name: 'client-shop-deals' },
            },
            {
                name: 'NAVIGATION.MY_PRODUCTS',
                icon: 'inbox-arrow',
                to: { name: 'client-my-products' },
                header: '-',
            },
            {
                name: 'GLOBAL.CONTRACTS',
                to: { name: `client-contracts` },
                icon: 'contract',
            },
            {
                name: 'NAVIGATION.BOOKKEEPING',
                to: { name: `client-bookkeeping` },
                icon: 'contract',
            },
            {
                name: 'NAVIGATION.BANKING',
                to: { name: 'start-banking' },
                icon: 'banknotes',
            },
        ];

        localItems.push({
            name: 'NAVIGATION.COMPANY_DETAILS',
            to: { name: `client-company-details` },
            icon: 'contract',
        });

        // Bookkeeper-owned tenants provide bookkeeping themselves, so hide the bookkeeping entry.
        if (applicationStore.info?.tenant.isBookkeeper) {
            return localItems.filter((item) => item.to?.name !== 'client-bookkeeping');
        }

        return localItems;
    });

    const items = computed<MenuItemsType[]>(() => {
        if (!userStore.sessionId) {
            return [];
        }

        if (userStore.isSupport) {
            return support.value;
        } else if (userStore.isNotary) {
            return notary.value;
        } else if (userStore.isAdvisor) {
            return bookkeeper.value;
        } else if (userStore.isClient) {
            return client.value;
        }

        return [];
    });

    return { items };
}
